import React, { useCallback, useEffect, useMemo } from 'react';
import { List, Modal, Spin } from 'antd';
import { useSelector } from 'react-redux';
import { AvailableModals, closeModal } from './modalSlice';
import { ReduxState, useAppDispatch } from '../../store';
import {
  forcedTitleAsCategory,
  renderer,
} from './CategoryExceptionsModal/categoryExceptionsModalLib';
import { useExceptions } from '../../hooks/useExceptions';
import { ColumnScroll } from '@ydistri/ds';
import { ConfigurationSectionType, SkuListConfigurationFlag, SkuType } from '@ydistri/api-sdk';
import { useDeleteCalculationConfigurationMutation } from '../../apis/apiGlobalConfiguration';
import { useTemplateOrCalculation } from '../../hooks/useTemplateOrCalculation';
import { PartialExceptionExpanded } from '../../lib/categories/categoryTypes';

export const MODAL_HEIGHT = 'auto';
const modalId = AvailableModals.REMOVABLE_CONFIG_DELETE;

const RemovableConfigDeleteModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const templateOrCalculation = useTemplateOrCalculation();
  const [deleteCalculationConfiguration, { isLoading: clearing }] =
    useDeleteCalculationConfigurationMutation();
  const configurationDefinition = useSelector(
    (state: ReduxState) => state.modal.modalData[modalId]?.configurationDefinition,
  );
  const fieldType = configurationDefinition?.fieldType;

  const handleClose = useCallback(() => dispatch(closeModal()), [dispatch]);

  const exceptions = useExceptions();

  const categorySorter = (a: PartialExceptionExpanded, b: PartialExceptionExpanded) => {
    return a.titleFull.localeCompare(b.titleFull);
  };

  const deleteCalculationConfigurationSkuListFlag = useCallback(() => {
    if (configurationDefinition) {
      deleteCalculationConfiguration({
        templateOrCalculation,
        request: {
          skuListConfigurationFlagIds:
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- needed
            configurationDefinition.fieldType as unknown as SkuListConfigurationFlag,
        },
      }).then(() => {
        handleClose();
      });
    }
  }, [configurationDefinition, deleteCalculationConfiguration, handleClose, templateOrCalculation]);

  const finalExceptions = useMemo(() => {
    if (!fieldType) return [];
    const hasCategory: Record<number, true | undefined> = {};
    return exceptions
      .filter(e => {
        if (e.fieldType !== fieldType || hasCategory[e.category.id]) return false;
        if (
          configurationDefinition.sectionType !== ConfigurationSectionType.PotentialTarget ||
          e.skuType === SkuType.DeadStock
        ) {
          hasCategory[e.category.id] = true;
          return true;
        }

        return false;
      })
      .sort(categorySorter);
  }, [fieldType, exceptions, configurationDefinition]);

  const renderDefinitionByField = useCallback(
    (exceptionExpanded: PartialExceptionExpanded) => {
      if (!configurationDefinition) return null;
      return renderer(
        exceptionExpanded,
        forcedTitleAsCategory,
        [configurationDefinition],
        undefined,
        false,
      );
    },
    [configurationDefinition],
  );

  const content = useMemo(() => {
    if (!configurationDefinition) return null;
    if (finalExceptions.length === 0) return null;

    return (
      <Spin spinning={false}>
        <List
          dataSource={finalExceptions}
          loading={false}
          size="small"
          renderItem={renderDefinitionByField}
          data-testid={`ConfigurationGroupExceptions-${fieldType}`}
        />
      </Spin>
    );
  }, [configurationDefinition, fieldType, finalExceptions, renderDefinitionByField]);

  useEffect(() => {
    if (finalExceptions.length === 0) {
      deleteCalculationConfigurationSkuListFlag();
    }
  }, [finalExceptions, deleteCalculationConfigurationSkuListFlag]);

  if (finalExceptions.length === 0) return null;

  return (
    <Modal
      title={`Remove configuration - ${configurationDefinition?.title ?? 'undefined'}`}
      centered
      open={true}
      onOk={deleteCalculationConfigurationSkuListFlag}
      okText="Remove"
      okButtonProps={{ loading: clearing }}
      onCancel={handleClose}
      width="50%"
    >
      There are exceptions set for this configuration and they will be cleared when you proceed.
      <ColumnScroll $height={MODAL_HEIGHT} $gap="2rem">
        {content}
      </ColumnScroll>
    </Modal>
  );
};

export default RemovableConfigDeleteModal;
